import { useEffect, useState, useCallback } from 'react';
import { useProjectViewStore } from '@/stores/projectView';
import { saveDocument as saveDocumentApi } from '@/api/tasks';
import type { SharePointItem } from '@/types/odoo';

const SP_BASE = 'https://magnumheatinggroup.sharepoint.com/sites/THDT/Gedeelde%20documenten/';

function getSharePointUrl(itemPath: string): string {
  const relativePath = itemPath.replace(/^Gedeelde documenten\//, '');
  return SP_BASE + relativePath.split('/').map(encodeURIComponent).join('/');
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function FolderIcon() {
  return (
    <svg className="w-5 h-5 text-td-blue shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function FileDocIcon() {
  return (
    <svg className="w-5 h-5 text-td-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

interface DocumentenTabProps {
  projectId: number;
  projectName: string;
}

export function DocumentenTab({ projectId }: DocumentenTabProps) {
  const {
    documentItems,
    documentBreadcrumb,
    documentFound,
    documentProjectId,
    documentLoading,
    documentError,
    fetchDocumentSearch,
    fetchDocumentList,
  } = useProjectViewStore();

  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (documentProjectId !== projectId) {
      fetchDocumentSearch(projectId);
    }
  }, [projectId, documentProjectId, fetchDocumentSearch]);

  const handleFolderClick = useCallback(
    (folder: SharePointItem) => {
      fetchDocumentList(projectId, folder.path);
    },
    [projectId, fetchDocumentList],
  );

  const handleBreadcrumbClick = useCallback(
    (index: number) => {
      if (!documentBreadcrumb) return;
      // Build path from breadcrumb segments up to the clicked index
      const path = documentBreadcrumb.slice(0, index + 1).join('/');
      fetchDocumentList(projectId, path);
    },
    [projectId, documentBreadcrumb, fetchDocumentList],
  );

  const handleSave = useCallback(
    async (item: SharePointItem) => {
      setSavingKey(item.path);
      setSaveError(null);
      try {
        const { url } = await saveDocumentApi(projectId, item.path, item.name);
        // Download the file via the returned URL
        const a = document.createElement('a');
        a.href = url;
        a.download = item.name;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
      } finally {
        setSavingKey(null);
      }
    },
    [projectId],
  );

  // Loading state
  if (documentLoading && !documentItems) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Laden...</span>
        </div>
        <p className="text-td-muted text-sm mt-3">Documenten laden...</p>
      </div>
    );
  }

  // Error state
  if (documentError) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
        <p className="font-semibold">Fout bij laden</p>
        <p className="text-sm mt-1">{documentError}</p>
      </div>
    );
  }

  // Not found state
  if (documentFound === false) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-td-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <p className="text-td-muted text-sm">Geen projectmap gevonden op SharePoint</p>
      </div>
    );
  }

  // Sort: folders first, then files
  const sorted = [...(documentItems ?? [])].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    return a.name.localeCompare(b.name, 'nl');
  });

  return (
    <div>
      {/* Breadcrumb */}
      {documentBreadcrumb && documentBreadcrumb.length > 0 && (
        <nav className="mb-4 flex items-center gap-1 text-sm overflow-x-auto" aria-label="Mapnavigatie">
          {documentBreadcrumb.map((segment, i) => {
            const isLast = i === documentBreadcrumb.length - 1;
            return (
              <span key={i} className="flex items-center gap-1 whitespace-nowrap">
                {i > 0 && <span className="text-td-muted" aria-hidden="true">/</span>}
                {isLast ? (
                  <span className="font-semibold text-td-navy">{segment}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleBreadcrumbClick(i)}
                    className="text-td-blue hover:underline"
                  >
                    {segment}
                  </button>
                )}
              </span>
            );
          })}
        </nav>
      )}

      {/* Save error */}
      {saveError && (
        <div className="mb-3 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
          <span>{saveError}</span>
          <button type="button" onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 ml-2 font-bold">&times;</button>
        </div>
      )}

      {/* Loading overlay for navigation within folder */}
      {documentLoading && documentItems && (
        <div className="flex items-center gap-2 mb-3 text-sm text-td-muted">
          <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
          Laden...
        </div>
      )}

      {/* Empty folder */}
      {sorted.length === 0 && !documentLoading && (
        <div className="text-center py-12">
          <p className="text-td-muted text-sm">Deze map is leeg</p>
        </div>
      )}

      {/* File/folder list */}
      {sorted.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Header - desktop only */}
          <div className="hidden md:grid md:grid-cols-[1fr_100px_120px_140px] gap-4 px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-bold text-td-muted uppercase tracking-wide">Naam</span>
            <span className="text-xs font-bold text-td-muted uppercase tracking-wide">Grootte</span>
            <span className="text-xs font-bold text-td-muted uppercase tracking-wide">Gewijzigd</span>
            <span className="text-xs font-bold text-td-muted uppercase tracking-wide text-right">Acties</span>
          </div>

          {sorted.map((item) => (
            <div
              key={item.path}
              className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
            >
              {/* Mobile layout */}
              <div className="md:hidden">
                <div className="flex items-center gap-3">
                  {item.type === 'folder' ? <FolderIcon /> : <FileDocIcon />}
                  <div className="flex-1 min-w-0">
                    {item.type === 'folder' ? (
                      <button
                        type="button"
                        onClick={() => handleFolderClick(item)}
                        className="text-sm font-semibold text-td-blue hover:underline truncate block text-left"
                      >
                        {item.name}
                      </button>
                    ) : (
                      <span className="text-sm font-semibold text-td-navy truncate block">{item.name}</span>
                    )}
                    {item.type === 'file' && (
                      <span className="text-xs text-td-muted">
                        {[formatFileSize(item.size), formatDate(item.lastModified)].filter(Boolean).join(' - ')}
                      </span>
                    )}
                  </div>
                  {item.type === 'file' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => window.open(getSharePointUrl(item.path), '_blank')}
                        className="px-2.5 py-1.5 text-xs font-semibold text-td-blue border border-td-blue/30 rounded-lg hover:bg-td-blue/5 transition-colors"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(item)}
                        disabled={savingKey === item.path}
                        className="px-2.5 py-1.5 text-xs font-semibold text-td-navy bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {savingKey === item.path && (
                          <div className="w-3 h-3 border-2 border-td-navy/30 border-t-td-navy rounded-full animate-spin" />
                        )}
                        Opslaan
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop layout */}
              <div className="hidden md:grid md:grid-cols-[1fr_100px_120px_140px] gap-4 items-center">
                <div className="flex items-center gap-3 min-w-0">
                  {item.type === 'folder' ? <FolderIcon /> : <FileDocIcon />}
                  {item.type === 'folder' ? (
                    <button
                      type="button"
                      onClick={() => handleFolderClick(item)}
                      className="text-sm font-semibold text-td-blue hover:underline truncate text-left"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <span className="text-sm font-semibold text-td-navy truncate">{item.name}</span>
                  )}
                </div>
                <span className="text-sm text-td-muted">{item.type === 'file' ? formatFileSize(item.size) : ''}</span>
                <span className="text-sm text-td-muted">{item.type === 'file' ? formatDate(item.lastModified) : ''}</span>
                <div className="flex items-center justify-end gap-2">
                  {item.type === 'file' && (
                    <>
                      <button
                        type="button"
                        onClick={() => window.open(getSharePointUrl(item.path), '_blank')}
                        className="px-2.5 py-1.5 text-xs font-semibold text-td-blue border border-td-blue/30 rounded-lg hover:bg-td-blue/5 transition-colors"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(item)}
                        disabled={savingKey === item.path}
                        className="px-2.5 py-1.5 text-xs font-semibold text-td-navy bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {savingKey === item.path && (
                          <div className="w-3 h-3 border-2 border-td-navy/30 border-t-td-navy rounded-full animate-spin" />
                        )}
                        Opslaan
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
